import moment from 'moment';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { v4 as uuid4 } from 'uuid';

import { MTMDatePickerDirective } from '../../../directives/mtm-datepicker.directive';
import { AuthService } from '../../../services/auth.service';
import { CustomDateParserFormatter } from '../../../services/datepicker-adapter';
import { DateHelperService } from '../../../services/date-helper.service';
import { GeneralUtilsService } from '../../../services/general-utils.service';
import { HelperService } from '../../../services/helper.service';
import { OverlayService } from '../../../services/overlayService';
import { ProjectService } from '../../../services/project.service';
import { ReviewLinkService } from '../../../services/review-link.service';
import { SubsectionService } from '../../../services/subsection.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { LoadingDataComponent } from '../../loading-data/loading-data.component';
import { SelectedInvitersChange } from './mtm-inviter-dropdown/mtm-inviter-dropdown.component';
import {
  OptionInfo,
  TopicInfo,
  HttpErrorResponse,
  ReviewLinkAssetItem,
  ReviewLinkData,
  SaveReviewLinkArgs,
  TopicInfoWithOptional,
  SimpleUser,
  ReviewLinkSubSectionAsset,
  ReviewLinkSetParamsArgs,
  ProjectSubsection,
  ProjectSection,
} from '../../../interfaces';

@Component({
  selector: 'review-link-settings',
  templateUrl: './review-link-settings.component.html',
  styleUrl: './review-link-settings.component.scss',
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  standalone: false,
})
export class ReviewUiLinkSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('linkExpirationDateElement', { static: true })
  linkExpirationDateElement: MTMDatePickerDirective;
  @ViewChild('loadingRef', { static: true }) loadingRef: LoadingDataComponent;

  section: ProjectSection;
  subSection: ProjectSubsection;
  activeTab: string = 'share';
  shortedSharingUrl: string;
  items: unknown[] = [];
  subSectionAssets: ReviewLinkSubSectionAsset[] = [];
  selectedAssets: ReviewLinkSubSectionAsset[] = [];
  inviteData: SimpleUser[] = [];
  invitedUsers: SimpleUser[] = [];
  reviewLinkId: string = uuid4();
  passwordProtection: string | null = null;
  messageToUsers: string;
  formSubmitted: boolean = false;
  formError: Record<string, boolean> = {};
  expirationDate: number | string | null = null;
  linkExpirationDate: NgbDateStruct | null = null;
  blacklistedParticipants: string[] = [];
  managedUsers: SimpleUser[] = [];
  updatedReviewLink: ReviewLinkData | undefined;
  isBusy: boolean = false;
  linkCopied: string;
  isExpired: boolean = false;
  topics: (TopicInfo & { name?: string; id?: string })[] = [];
  isRestricted: boolean = false;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  isLoading: boolean;
  reviewLink: ReviewLinkData | undefined;
  editMode: boolean = false;
  minDate: NgbDateStruct;
  private readonly checkReviewLinkRequest: Subject<boolean> = new Subject<boolean>();
  private readonly createReviewLinkRequest: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly projectService: ProjectService,
    public activeModal: NgbActiveModal,
    private readonly overlayService: OverlayService,
    private readonly reviewLinkService: ReviewLinkService,
    private readonly translatePipe: TranslatePipe,
    private readonly authService: AuthService,
    private readonly generalUtilsService: GeneralUtilsService,
    private readonly helperService: HelperService,
    private readonly dateHelperService: DateHelperService,
    private readonly subsectionService: SubsectionService
  ) {
    const today: Date = new Date();
    this.minDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
  }

  ngOnInit(): void {
    this.inviteData = this.projectService.project.participants;
    this.shortedSharingUrl = this.helperService.getCurrentOrigin() + '/review/' + this.reviewLinkId;
    this.linkCopied = this.translatePipe.transform('linkCopied');
    this.isLoading = true;
    this.loadingRef.showLoading();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public settingsTabSelect(tab: string): void {
    this.activeTab = tab;
  }

  public closeModal(force?: boolean): void {
    if (this.isBusy && !force) {
      return;
    }
    this.activeModal.close({ data: this.updatedReviewLink });
  }

  public saveReviewLink(args: SaveReviewLinkArgs = {}): void {
    if (!args.clear) {
      return;
    }
    this.createReviewLinkRequest.next(true);
    this.checkReviewLinkRequest.next(true);
    this.formSubmitted = true;
    this.formError = {};
    const assets: ReviewLinkSubSectionAsset[] = args.assets ?? this.selectedAssets ?? [];
    const reviewLinkAssets: ReviewLinkAssetItem[] = [];
    for (const asset of assets) {
      for (const ver of asset.simpleVersions ?? []) {
        if (ver.isSelected && this.section && this.subSection) {
          reviewLinkAssets.push({
            sectionId: this.section.id,
            sectionName: this.section.name,
            subSectionId: this.subSection.id,
            subSectionName: this.subSection.name,
            version: ver.versionNumber,
            name: asset.title,
            itemId: asset.optionId,
          });
        }
      }
    }
    if (reviewLinkAssets.length === 0) {
      this.formError.assets = true;
    }
    if (!this.generalUtilsService.isObjectEmpty(this.formError)) {
      return;
    }
    if (!this.reviewLink) {
      const defaultDate: moment.Moment = moment().add(1, 'M');
      this.linkExpirationDate = {
        year: defaultDate.year(),
        month: defaultDate.month() + 1,
        day: defaultDate.date(),
      };
    }
    const expirationDate: number | null = this.linkExpirationDate
      ? moment(this.dateHelperService.getDateFromDatepicker(this.linkExpirationDate)).utc().valueOf()
      : null;
    this.isBusy = true;

    this.reviewLinkService
      .checkReviewLinkExistence({
        assets: reviewLinkAssets,
        creator: this.authService.getAuthUser().email,
        projectId: this.projectService.project.id,
      })
      .pipe(takeUntil(this.checkReviewLinkRequest))
      .subscribe({
        next: (res: ReviewLinkData) => {
          this.reviewLinkId = res.id;
          this.reviewLink = res;
          this.createReviewLink(reviewLinkAssets, expirationDate, args);
        },
        error: (err: HttpErrorResponse) => {
          if (err?.errorCode === 'NOT_FOUND') {
            this.reviewLinkId = uuid4();
            this.createReviewLink(reviewLinkAssets, expirationDate, args);
          } else {
            this.overlayService.showError(err?.message, err?.errorCode);
            this.isBusy = false;
          }
        },
      });
  }

  public assetChangeHandler(assets: ReviewLinkSubSectionAsset[]): void {
    this.selectedAssets = assets;
    this.saveReviewLink({ clear: true });
  }

  private createReviewLink(
    reviewLinkAssets: ReviewLinkAssetItem[],
    expirationDate: number | null,
    args: SaveReviewLinkArgs
  ): void {
    this.isBusy = true;
    const participants: string[] = [...((args?.inviters ?? this.invitedUsers) || []), ...(this.managedUsers || [])].map(
      (usr: SimpleUser) => usr.email
    );
    this.reviewLinkService
      .createReviewLinks(this.projectService.project.id, {
        assets: reviewLinkAssets,
        blacklistedParticipants: this.blacklistedParticipants,
        creator: this.authService.getAuthUser().email,
        expiration: expirationDate,
        id: this.reviewLinkId,
        link: this.shortedSharingUrl,
        isRestricted: this.isRestricted,
        message: this.messageToUsers,
        participants,
        password: this.passwordProtection || null,
        projectId: this.projectService.project.id,
      })
      .pipe(takeUntil(this.createReviewLinkRequest))
      .subscribe({
        next: (res: ReviewLinkData) => {
          if (!args.clear) {
            res.participants = [...((args?.inviters ?? this.invitedUsers) || []), ...(this.managedUsers || [])].map(
              (usr: SimpleUser) => usr.email
            );
            this.updatedReviewLink = res;
            this.setParams({
              reviewLink: res,
              clear: args.clear,
              updated: true,
            });
          }
          this.reviewLink = res;
          if (args.closeModal) {
            this.overlayService.showSuccess('Data saved.', 'Success');
            this.activeModal.close({ data: this.reviewLink });
          }
          this.isBusy = false;
        },
        error: (err: HttpErrorResponse) => {
          this.overlayService.showError(err?.message, err?.errorCode);
          this.isBusy = false;
        },
      });
  }

  public showTooltip(e: Event): void {
    const target: HTMLElement = (e.target as HTMLElement) ?? (e.currentTarget as HTMLElement);
    const element: HTMLElement | null = target.querySelector?.('.mtm-icon-share-link-red');
    if (element) {
      element.classList.add('clipboard-tooltipped');
      setTimeout(() => {
        element.classList.remove('clipboard-tooltipped');
      }, 3000);
    }
  }

  get reviewLinkUrl(): string {
    return this.reviewLink ? `${this.helperService.getCurrentOrigin()}/review/${this.reviewLinkId}` : '';
  }

  public removeManagedUser(user: SimpleUser): void {
    if (this.isBusy) {
      return;
    }
    this.managedUsers = this.managedUsers.filter((managedUser: SimpleUser) => user.email !== managedUser.email);
    this.blacklistedParticipants.push(user.email);
    this.saveReviewLink({});
  }

  private getSubSectionTopic(): Observable<TopicInfo[]> {
    return this.subsectionService.getTopics(this.projectService.project.id, this.section.id, this.subSection.id);
  }

  public setParams(args: ReviewLinkSetParamsArgs): void {
    if (args.fromExisting) {
      this.editMode = true;
    }
    if (args.reviewLink) {
      this.loadReviewLinkParams(args);
    } else {
      this.section = this.projectService.project.sectionsId[args.section];
      this.subSection = this.projectService.project.subsectionsId[args.subSection];
      this.subSectionAssets = (args.assets ?? []).sort(
        (a: ReviewLinkSubSectionAsset, b: ReviewLinkSubSectionAsset) => (a.order ?? 0) - (b.order ?? 0)
      );
      this.selectedAssets = this.subSectionAssets.filter(
        (asset: ReviewLinkSubSectionAsset) => asset.optionId === args.parentItem.optionId
      );
      this.getSubSectionTopic()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (res: TopicInfo[]) => {
            this.topics = (res as TopicInfoWithOptional[]).map((t: TopicInfoWithOptional) => {
              t.title = t.title ?? t.name;
              t.topicId = t.topicId ?? t.id;
              return t;
            });
            this.setReviewLinkVersioningAssets(undefined, args);
            this.isLoading = false;
            this.loadingRef.hide();
          },
          error: () => {
            this.isLoading = false;
            this.loadingRef.hide();
          },
        });
    }
  }

  private loadReviewLinkParams(args: ReviewLinkSetParamsArgs): void {
    const { reviewLink } = args;
    this.reviewLink = reviewLink;
    this.isRestricted = reviewLink.isRestricted;
    this.section = this.projectService.project.sectionsId[reviewLink.assets[0].sectionId];
    this.subSection = this.projectService.project.subsectionsId[reviewLink.assets[0].subSectionId];
    this.reviewLinkId = reviewLink.id;

    const onError: () => void = (): void => {
      this.isLoading = false;
      this.loadingRef.hide();
    };

    this.getSubSectionTopic()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap((res: TopicInfo[]) => {
          this.topics = (res as TopicInfoWithOptional[]).map((t: TopicInfoWithOptional) => {
            t.title = t.title ?? t.name;
            t.topicId = t.topicId ?? t.id;
            return t;
          });
          return this.getRequests(this.projectService.project.id, this.subSection);
        })
      )
      .subscribe({
        next: (resOptions: OptionInfo[]) => this.applyReviewLinkParamsFromOptions(args, resOptions),
        error: onError,
      });
  }

  private applyReviewLinkParamsFromOptions(args: ReviewLinkSetParamsArgs, resOptions: OptionInfo[]): void {
    const { reviewLink } = args;
    if (!reviewLink) {
      return;
    }
    if (args.clear || args.init) {
      this.messageToUsers = '';
      const data: SimpleUser[] = Array.from(
        new Map(
          [...(this.projectService.project.participants || []), ...(reviewLink.participantsUsers || [])].map(
            (user: SimpleUser) => [user.email, user]
          )
        ).values()
      );
      this.managedUsers = data.filter((usr: SimpleUser) =>
        reviewLink.participants.some((u: string) => u === usr.email && usr.email !== reviewLink.creatorUser.email)
      );
    }
    this.invitedUsers = reviewLink.participantsUsers.filter((p: SimpleUser) => p.username !== reviewLink.creator);
    this.blacklistedParticipants = reviewLink.blacklistedParticipants;
    if (!args.updated) {
      this.subSectionAssets = (resOptions as ReviewLinkSubSectionAsset[]).sort(
        (a: ReviewLinkSubSectionAsset, b: ReviewLinkSubSectionAsset) => (a.order ?? 0) - (b.order ?? 0)
      );
      this.selectedAssets = this.subSectionAssets.filter((subSectionAsset: ReviewLinkSubSectionAsset) =>
        reviewLink.assets.some((asset: { itemId: string }) => asset.itemId === subSectionAsset.optionId)
      );
    }
    this.inviteData = this.inviteData.filter(
      (data: SimpleUser) => !this.managedUsers.some((managedUser: SimpleUser) => managedUser.email === data.email)
    );
    this.shortedSharingUrl = this.helperService.getCurrentOrigin() + '/review/' + reviewLink.id;
    this.passwordProtection = reviewLink.password ?? null;
    if (reviewLink.expiration && !args.updated) {
      this.linkExpirationDate = {
        year: Number.parseInt(moment.unix(reviewLink.expiration / 1000).format('YYYY'), 10),
        month: Number.parseInt(moment.unix(reviewLink.expiration / 1000).format('M'), 10),
        day: Number.parseInt(moment.unix(reviewLink.expiration / 1000).format('D'), 10),
      };
    }
    if (!args.updated) {
      this.setReviewLinkVersioningAssets(reviewLink);
    }
    this.isLoading = false;
    this.loadingRef.hide();
  }

  reviewLinkVersionAssets: {
    label: string;
    value: string;
    topics: Array<{
      label: string;
      value: string;
      assets: ReviewLinkSubSectionAsset[];
      isSelected?: boolean;
      isExpanded?: boolean;
    }>;
  };

  private setReviewLinkVersioningAssets(reviewLink?: ReviewLinkData | null, args?: ReviewLinkSetParamsArgs): void {
    this.initReviewLinkVersionAssets();
    for (const asset of this.subSectionAssets) {
      this.prepareAsset(asset);
      if (asset.isSelected) {
        this.handleSelectedAsset(asset, reviewLink, args);
      } else {
        this.resetAsset(asset);
      }
    }
    if (!reviewLink) {
      this.saveReviewLink({ clear: true });
    }
  }

  private initReviewLinkVersionAssets(): void {
    this.reviewLinkVersionAssets = {
      label: this.subSection.name,
      value: this.subSection.id,
      topics: this.topics.map((t: TopicInfoWithOptional) => ({
        label: t.title,
        value: t.topicId,
        assets: this.getTopicAssets(t),
      })),
    };
  }

  private prepareAsset(asset: ReviewLinkSubSectionAsset): void {
    asset.isSelected = this.selectedAssets.some(
      (selected: ReviewLinkSubSectionAsset) => asset.optionId === selected.optionId
    );
    asset.simpleVersions = asset.simpleVersions ?? (asset.versions as ReviewLinkSubSectionAsset['simpleVersions']);
    if (this.generalUtilsService.isObjectEmpty(asset.simpleVersions)) {
      asset.simpleVersions = [];
    }
  }

  private handleSelectedAsset(
    asset: ReviewLinkSubSectionAsset,
    reviewLink?: ReviewLinkData | null,
    args?: ReviewLinkSetParamsArgs
  ): void {
    asset.isExpanded = true;
    this.selectAssetVersions(asset, reviewLink, args);
    this.updateTopicsSelection(asset);
  }

  private selectAssetVersions(
    asset: ReviewLinkSubSectionAsset,
    reviewLink?: ReviewLinkData | null,
    args?: ReviewLinkSetParamsArgs
  ): void {
    for (const v of asset.simpleVersions) {
      v.isSelected = reviewLink
        ? reviewLink.assets.some(
            (a: { itemId: string; version: number }) => a.itemId === asset.optionId && v.versionNumber === a.version
          )
        : v.versionNumber === args?.version;
    }
  }

  private updateTopicsSelection(asset: ReviewLinkSubSectionAsset): void {
    for (const topic of this.reviewLinkVersionAssets.topics) {
      topic.isSelected = topic.isExpanded =
        topic.assets.some((a: ReviewLinkSubSectionAsset) => !!a.isSelected) || asset.optionId === topic.value;
    }
  }

  private resetAsset(asset: ReviewLinkSubSectionAsset): void {
    for (const v of asset.simpleVersions) {
      v.isSelected = false;
    }
    asset.isSelected = false;
    asset.isExpanded = false;
  }

  private getTopicAssets(t: TopicInfoWithOptional): ReviewLinkSubSectionAsset[] {
    return this.subSectionAssets.filter((asset: ReviewLinkSubSectionAsset) => asset.topicId === t.topicId);
  }

  public selectedInvitersChange(data: SelectedInvitersChange): void {
    this.blacklistedParticipants = this.blacklistedParticipants.filter((email: string) => email !== data.newData);
    this.managedUsers = this.managedUsers.filter((m: SimpleUser) => m.email !== data.removedData?.email);
    this.isRestricted = this.invitedUsers.length > 0;
    this.saveReviewLink({});
  }

  private getRequests(projectId: string, subSectionData: ProjectSubsection): Observable<OptionInfo[]> {
    return this.subsectionService.getOptions(projectId, subSectionData.projectSectionId, subSectionData.id);
  }

  public selectUserType(isRestricted: boolean): void {
    this.isRestricted = isRestricted;
    if (!isRestricted) {
      this.invitedUsers = [];
    }
    this.saveReviewLink({ clear: true });
  }
}
